/**
 * Menu controller.
 *
 * This class controls the site menu and search functions.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * Class Menu
 */
class Menu {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {

        // Store the status of the mobile overlay.
        this.mobileOverlayOpen = false;
    }

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {

        // Stores info is a desktop sub-menu is currently opened or not.
        this.$desktopSubMenuIsOpen = false;

        // The container for the main desktop menu. Needed when checking the offset.
        this.desktopNavContainer = document.getElementById( 'main-nav' );

        // The mobile overlay toggle button.
        this.hamburger = document.getElementById( 'js-mobile-toggle-btn' );

        // The mobile full screen overlay that contains the mobile menu.
        this.mobileOverlay = document.getElementById( 'js-mobile-overlay' );

        // Get first and last focusable elements of the mobile menu..
        if ( this.mobileOverlay ) {

            const focusableElements = this.mobileOverlay.querySelectorAll( 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]' );
            if ( focusableElements.length ) {
                const focusableElementsArr = Array.prototype.slice.call( focusableElements );

                this.firstMobileMenuLink = focusableElementsArr[0];
                this.lastMobileMenuLink = focusableElementsArr[focusableElementsArr.length - 1];
            }
        }

        // The toggler button for the desktop navigation's search form container.
        this.searchFormToggler = document.getElementById( 'header-search-toggle' );

        // The container for the desktop navigation's search form.
        this.searchFormContainer = document.getElementById( 'header-search' );

        // The text input area inside the searchFormContainer
        this.searchFormDesktopInput = document.getElementById( 'header-search__form__input' );

        // Each top level menu item that has children.
        this.menuParents = Theme.Common.$( '.js-main-menu-nav .menu-item-has-children' );

        // Each submenu inside the top level menu items that have children.
        if ( this.menuParents.length ) {
            this.menuSubMenus = Theme.Common.childrenFromEach( this.menuParents, '.js-sub-menu' );
            this.subMenuToggleButtons = Theme.Common.childrenFromEach( this.menuParents, '.main-nav__submenu-toggle' );
        }
    }

    /**
     * All common events
     * @return {void}
     */
    events() {
        document.addEventListener( 'keydown', evt => this.keyDownHandler( evt ) );

        // Check if user has clicked outside an open menu
        document.addEventListener( 'click', e => this.checkOutsideMenuClick( e ) );
    }

    /**
     * Handle keydown events.
     *
     * @param {object} evt The key push event.
     * @return {void}
     */
    keyDownHandler( evt ) {

        // Return early if no mobile overlay exists.
        if ( ! this.mobileOverlay ) {
            return;
        }

        // Return early if mobile overlay not open.
        if ( ! this.mobileOverlay.classList.contains( 'js-open' ) ) {
            return;
        }

        evt = evt || window.event;

        const KEY_TAB = 9;
        const KEY_ESC = 27;

        switch ( evt.keyCode ) {
            case KEY_TAB:

                // Return early if no hamburgers found.
                if ( ! this.hamburger ) {
                    return;
                }

                this.handleTabKeyEvent( evt );

                break;
            case KEY_ESC:
                this.toggleMobileOverlay();
                break;
            default:
                break;
        } // end switch
    }

    /**
     * Handle tab key pushes inside modal.
     *
     * @param {object} evt The key push event.
     * @return {void}
     */
    handleTabKeyEvent( evt ) {

        if ( ! this.firstMobileMenuLink || ! this.hamburger ) {
            return;
        }

        // If shift + tab pushed.
        if ( evt.shiftKey ) {

            // Focus on hamburger element if focus was on the first element.
            if ( document.activeElement === this.firstMobileMenuLink ) {
                evt.preventDefault();
                this.hamburger.focus();
                return;
            }

            // Focus on last element if focus was on the hamburger element.
            if ( document.activeElement === this.hamburger ) {
                evt.preventDefault();
                this.lastMobileMenuLink.focus();
                return;
            }
        } else {

            // Focus on hamburger element if focus was on the last element.
            if ( document.activeElement === this.lastMobileMenuLink ) {

                evt.preventDefault();
                this.hamburger.focus();
                return;
            }

            // Focus on first element if focus was on the hamburger element.
            if ( document.activeElement === this.hamburger ) {
                evt.preventDefault();
                this.firstMobileMenuLink.focus();
                return;
            }
        }
    }

    /**
     * Toggles the search form container open and closed when the
     * magnifying class icon is clicked.
     * @return {void}
     */
    toggleSearchForm() {

        // Return early if missing required objects.
        if ( ! this.searchFormContainer || ! this.searchFormToggler ) {
            return;
        }

        // When search is opened, reset parent list items and
        // sub menus back to their default, closed states. Set the
        // aria-hidden state back to true when the menus are closed.
        this.closeAllNavMenus();

        // Toggle the search form container's active state.
        this.searchFormContainer.classList.toggle( 'js-active' );

        // Get the toggler's aria-expanded current state and set a new opposite state to it.
        // Also set the opened container's aria-hidden state to the new value's opposite.
        const ariaExpandedState = this.searchFormToggler.getAttribute( 'aria-expanded' ) === 'false' ? true : false;
        this.searchFormToggler.setAttribute( 'aria-expanded', ariaExpandedState );
        this.searchFormContainer.setAttribute( 'aria-hidden', ! ariaExpandedState );

        // Set focus to search input if search was opened with mouse. The body classlist is maybe not the most optimal
        // way of checking the ui-type but will have to do for now.
        if ( document.body.classList.contains( 'js-ui-is-mouse' ) && ariaExpandedState && this.searchFormDesktopInput ) {

            // Set a small delay to the focusing of the form input, because of the CSS transition.
            setTimeout( () => {
                this.searchFormDesktopInput.focus();
            }, 500 );
        }
    }

    /**
     * Toggles the mobile menu overlay open and closed
     * @return {void}
     */
    toggleMobileOverlay() {

        // Return early if missing required elements.
        if ( ! this.hamburger || ! this.mobileOverlay ) {
            return;
        }

        // Get the toggler's aria-expanded current state and set a new opposite state to it.
        // Also set the opened container's aria-hidden state to the new value's opposite.
        const ariaExpandedState = this.hamburger.getAttribute( 'aria-expanded' ) === 'false' ? true : false;
        this.hamburger.setAttribute( 'aria-expanded', ariaExpandedState );
        this.mobileOverlay.setAttribute( 'aria-hidden', ! ariaExpandedState );

        // Toggle the overlay's and the hamburger's active states.
        this.mobileOverlay.classList.toggle( 'js-open' );
        this.hamburger.classList.toggle( 'is-active' );

        // Toggle the class on site body that prevents background-scrolling.
        document.body.classList.toggle( 'mobile-overlay-is-open' );

        // Return early if hamburger closed.
        if ( ! this.mobileOverlay.classList.contains( 'js-open' ) ) {

            // Remove styles and events that prevented the body from scrolling when the overlay was open.
            enableBodyScroll( this.mobileOverlay );

            this.mobileOverlayOpen = false;
            this.hamburger.focus();
            return;
        }

        // Ads styles and events that prevents the body from scrolling when the overlay is open.
        disableBodyScroll( this.mobileOverlay );

        this.mobileOverlayOpen = true;
    }

    /**
     * Toggles the sub menus open and closed.
     *
     * @param {object} e  The click event.
     * @param {object} el The captured event target element.
     * @return {void}
     * @memberof Menu
     */
    toggleSubMenu( e, el ) {

        // The clicked element
        const clickedElement = el;

        // Get the parent element of the clicked element.
        let clickedParent = clickedElement.parentNode;
        if ( ! clickedParent.classList.contains( 'js-menu-item' ) ) {
            clickedParent = clickedParent.parentNode;
        }

        // Get the submenu inside the parent.
        const subMenu = Theme.Common.$1( '.js-sub-menu', clickedParent );

        // Get the siblings of the clicked menu
        const siblingParents = Theme.Common.siblings( clickedParent, 'menu-item-has-children' );

        // Get the submenus of the siblings of the clicked menu
        const siblingSubMenus = Theme.Common.childrenFromEach( siblingParents, '.js-sub-menu' );

        // Get all the sub-parents in the siblings of the clicked menu.
        const subParentsInSiblings = Theme.Common.childrenFromEach( siblingParents, '.menu-item-has-children' );

        Theme.Common.toggleBooleanAttribute( clickedElement, 'aria-expanded' );

        // Toggle the clicked parent's active state and its sub menu's active state.
        clickedParent.classList.toggle( 'js-active' );
        if ( subMenu ) {

            this.checkSubMenuOverflow( subMenu, clickedParent );

            subMenu.classList.toggle( 'js-active' );

            this.maybeCloseChildSubMenus( subMenu, clickedParent );

            // Toggle the clicked parent's sub menu's aria-hidden state.
            Theme.Common.toggleAriaHidden( subMenu );
        }

        // Remove the sibling parents' active states and their sub menus' active states.
        // Set the aria-hidden state to true when the sub menus are closed.
        Theme.Common.removeClassFromAll( siblingParents, 'js-active' );
        Theme.Common.removeClassFromAll( siblingSubMenus, 'js-active' );
        Theme.Common.removeClassFromAll( subParentsInSiblings, 'js-active' );
        Theme.Common.setAttrOnAll( siblingSubMenus, 'aria-hidden', true );
    }

    /**
     * If submenu is a second level menu that is about to open, check if it will overlflow.
     * @param {object} subMenu The submenu to be checked.
     * @param {object} clickedParent The parent element of the submenu
     */
    checkSubMenuOverflow( subMenu, clickedParent ) {

        // Check that we are working with a second level (desktop) menu about to be opened.
        if ( subMenu.classList.contains( 'js-check-submenu-overflow--depth-2' ) &&
            ! subMenu.classList.contains( 'js-active' ) &&
            ! this.mobileOverlayOpen &&
            this.desktopNavContainer
        ) {

            // Store info that a submenu is open.
            this.$desktopSubMenuIsOpen = true;

            // Get the offset of the parent item.
            const parentOffset = clickedParent.getBoundingClientRect().left + window.scrollX;

            // Check if the submenu width combined with the parentOffset is larger than the container width.
            const overflows = this.desktopNavContainer.offsetWidth < ( parentOffset + subMenu.offsetWidth );

            // Add or remove class to submenu depending on if it will fit in container.
            if ( overflows ) {
                subMenu.classList.remove( 'main-nav__sub-menu--depth-2--not-edge' );
                subMenu.classList.add( 'main-nav__sub-menu--depth-2--at-edge' );
            } else {
                subMenu.classList.remove( 'main-nav__sub-menu--depth-2--at-edge' );
                subMenu.classList.add( 'main-nav__sub-menu--depth-2--not-edge' );
            }
        }
    }

    /**
     * Closes all child sub-menu parents and child sub-menus if submenu is a second level menu that was closed.
     * @param {object} subMenu The submenu to be checked.
     * @param {object} clickedParent The parent element of the submenu
     */
    maybeCloseChildSubMenus( subMenu, clickedParent ) {

        // Check that we are working with a second level menu that was closed.
        if ( subMenu.classList.contains( 'main-nav__sub-menu--depth-2' ) &&
            ! subMenu.classList.contains( 'js-active' )
        ) {

            // Store info that all menus are closed.
            this.$desktopSubMenuIsOpen = false;

            // Get all open child sub-menu parents.
            const childSubParents = Theme.Common.$( '.menu-item-has-children.js-active', clickedParent );

            // Get all open child sub-menus
            const childSubMenus = Theme.Common.$( '.js-sub-menu.js-active', clickedParent );

            // Get all open child sub-menu toggle-buttons.
            const childSubMenuToggleButtons = Theme.Common.$( '.main-nav__submenu-toggle[aria-expanded=true]', subMenu );

            Theme.Common.removeClassFromAll( childSubParents, 'js-active' );
            Theme.Common.removeClassFromAll( childSubMenus, 'js-active' );
            Theme.Common.setAttrOnAll( childSubMenus, 'aria-hidden', true );
            Theme.Common.setAttrOnAll( childSubMenuToggleButtons, 'aria-expanded', 'false' );
        }
    }

    /**
     * Resets all open main menus
     */
    closeAllNavMenus() {
        if ( this.menuParents && this.menuSubMenus ) {
            Theme.Common.removeClassFromAll( this.menuParents, 'js-active' );
            Theme.Common.removeClassFromAll( this.menuSubMenus, 'js-active' );
            Theme.Common.setAttrOnAll( this.menuSubMenus, 'aria-hidden', true );
            Theme.Common.setAttrOnAll( this.subMenuToggleButtons, 'aria-expanded', 'false' );

            // Store info that all menus are closed.
            this.$desktopSubMenuIsOpen = false;
        }
    }

    /**
     * Checks if clicked outside of opened menu.
     * @link https://stackoverflow.com/a/3028037
     *
     * @param {object} e Event object.
     * @return {void}
     */
    checkOutsideMenuClick( e ) {
        if ( ! this.$desktopSubMenuIsOpen ) {
            return;
        }

        // Close modal if the click event was not inside the modal__content-wrap os modal__btn items.
        if ( ! $( e.target ).closest( '#main-nav' ).length ) {
            this.closeAllNavMenus();
        }
    }

    /**
     * Hide and show mobile navigation on scroll
     *
     * @return {void}
     */
    mobileNavScroll() {
        let prevScrollpos = window.pageYOffset;

        document.addEventListener( 'scroll', function() {
            const currentScrollPos = window.pageYOffset;
            const navigation = $( '#mobile-nav-and-search__toggle' );

            // If user has scrolled over initial mobile nav height
            if ( currentScrollPos > 60 ) {

                // Hide or show mobile nav when scrolling
                if ( prevScrollpos > currentScrollPos ) {
                    navigation.removeClass( 'navigation-hide' );
                } else {
                    navigation.addClass( 'navigation-hide' );
                }

                prevScrollpos = currentScrollPos;
            }
        });
    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.mobileNavScroll();
        this.cache();
        this.events();
    }
}

// Export the class reference.
module.exports = Menu;



// WEBPACK FOOTER //
// ./assets/scripts/menu.js